import React from "react";
import {
  Card,
  Button,
  Row,
  Col,
  Typography,
  Spin,
  Descriptions,
  Avatar,
  Tag,
  Space,
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  UserOutlined,
  StarOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useOne } from "@refinedev/core";
import { Actor } from "@/types";
import dayjs from "dayjs";

const { Title, Text, Paragraph } = Typography;

export const ActorShow: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { data: actorData, isLoading } = useOne<Actor>({
    resource: "actors",
    id: id!,
  });

  if (isLoading) {
    return (
      <div style={{ padding: "24px", textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  const actor = actorData?.data;

  if (!actor) {
    return (
      <div style={{ padding: "24px" }}>
        <Card>
          <Text>Acteur introuvable</Text>
        </Card>
      </div>
    );
  }

  const getGenderLabel = (gender?: string) => {
    switch (gender) {
      case "MALE":
        return "Homme";
      case "FEMALE":
        return "Femme";
      case "OTHER":
        return "Autre";
      default:
        return "Non spécifié";
    }
  };

  const getGenderColor = (gender?: string) => {
    switch (gender) {
      case "MALE":
        return "blue";
      case "FEMALE":
        return "pink";
      default:
        return "default";
    }
  };

  return (
    <div style={{ padding: "24px" }}>
      <Card>
        <div style={{ marginBottom: 24 }}>
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/artists")}
            >
              Retour à la liste
            </Button>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => navigate(`/artists/edit/${id}`)}
            >
              Modifier
            </Button>
          </Space>
        </div>

        <Row gutter={24}>
          <Col span={6}>
            <div style={{ textAlign: "center" }}>
              {actor.profileImageUrl ? (
                <Avatar
                  size={200}
                  src={actor.profileImageUrl}
                  alt={actor.fullName}
                />
              ) : (
                <Avatar
                  size={200}
                  icon={<UserOutlined />}
                  style={{ backgroundColor: "#1890ff" }}
                />
              )}
              <Title level={3} style={{ marginTop: 16 }}>
                {actor.fullName}
              </Title>
              <Tag color={getGenderColor(actor.gender)}>
                {getGenderLabel(actor.gender)}
              </Tag>
              {actor.profession && (
                <Tag color="purple" style={{ marginTop: 8 }}>
                  {actor.profession}
                </Tag>
              )}
            </div>
          </Col>

          <Col span={18}>
            <Descriptions
              title="Informations générales"
              bordered
              column={2}
              size="middle"
            >
              <Descriptions.Item label="Prénom">
                {actor.firstName}
              </Descriptions.Item>
              <Descriptions.Item label="Nom">
                {actor.lastName}
              </Descriptions.Item>
              <Descriptions.Item label="Nom complet" span={2}>
                {actor.fullName}
              </Descriptions.Item>
              <Descriptions.Item label="Date de naissance">
                {actor.birthDate
                  ? dayjs(actor.birthDate).format("DD/MM/YYYY")
                  : "Non spécifié"}
              </Descriptions.Item>
              <Descriptions.Item label="Nationalité">
                {actor.nationality || "Non spécifié"}
              </Descriptions.Item>
              <Descriptions.Item label="Genre">
                <Tag color={getGenderColor(actor.gender)}>
                  {getGenderLabel(actor.gender)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Profession">
                {actor.profession || "Non spécifié"}
              </Descriptions.Item>
            </Descriptions>

            {actor.biography && (
              <Card
                title="Biographie"
                style={{ marginTop: 24 }}
                size="small"
              >
                <Paragraph>{actor.biography}</Paragraph>
              </Card>
            )}

            {(actor.movieCount !== undefined ||
              actor.rating !== undefined ||
              actor.viewCount !== undefined) && (
              <Card
                title="Statistiques"
                style={{ marginTop: 24 }}
                size="small"
              >
                <Row gutter={16}>
                  {actor.movieCount !== undefined && (
                    <Col span={8}>
                      <div style={{ textAlign: "center" }}>
                        <Title level={4}>{actor.movieCount}</Title>
                        <Text type="secondary">Films</Text>
                      </div>
                    </Col>
                  )}
                  {actor.rating !== undefined && (
                    <Col span={8}>
                      <div style={{ textAlign: "center" }}>
                        <Title level={4}>
                          <StarOutlined style={{ color: "#faad14" }} />{" "}
                          {actor.rating.toFixed(1)}
                        </Title>
                        <Text type="secondary">Note moyenne</Text>
                      </div>
                    </Col>
                  )}
                  {actor.viewCount !== undefined && (
                    <Col span={8}>
                      <div style={{ textAlign: "center" }}>
                        <Title level={4}>
                          {actor.viewCount.toLocaleString()}
                        </Title>
                        <Text type="secondary">Vues</Text>
                      </div>
                    </Col>
                  )}
                </Row>
              </Card>
            )}
          </Col>
        </Row>
      </Card>
    </div>
  );
};